import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ChallengeCard from './ChallengeCard';
import CreateChallengeModal from './CreateChallengeModal';
import ChallengeLeaderboard from './ChallengeLeaderboard';
import ConfirmDialog from '../ConfirmDialog';
import './ChallengesPage.css';

const ChallengesPage = () => {
  const { authFetch, user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null); // Will be set after loading
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

  const loadChallenges = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, myRes] = await Promise.all([
        authFetch('/api/challenges'),
        authFetch('/api/challenges/my')
      ]);

      let myData = [];
      if (allRes.ok) {
        const data = await allRes.json();
        setChallenges(data.challenges || []);
      }

      if (myRes.ok) {
        const data = await myRes.json();
        myData = data.challenges || [];
        setMyChallenges(myData);
      }

      // Default to "My Challenges" if user has any, otherwise "Public"
      setActiveTab(prev => prev || (myData.length > 0 ? 'my' : 'public'));
    } catch (err) {
      console.error('Failed to load challenges:', err);
      setActiveTab(prev => prev || 'public');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadChallenges();
  }, [loadChallenges]);

  const handleJoin = async (challengeId) => {
    try {
      const res = await authFetch(`/api/challenges/${challengeId}/join`, {
        method: 'POST'
      });

      if (res.ok) {
        loadChallenges();
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('Join failed:', res.status, errorData);
        alert(`Failed to join: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to join challenge:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleLeave = async (challengeId) => {
    try {
      const res = await authFetch(`/api/challenges/${challengeId}/leave`, {
        method: 'POST'
      });

      if (res.ok) {
        loadChallenges();
      }
    } catch (err) {
      console.error('Failed to leave challenge:', err);
    }
  };

  const handleViewLeaderboard = (challenge) => {
    setSelectedChallenge(challenge);
  };

  const handleChallengeCreated = () => {
    setShowCreateModal(false);
    setEditingChallenge(null);
    loadChallenges();
  };

  const handleEdit = (challenge) => {
    setEditingChallenge(challenge);
  };

  const handleDeleteClick = (challengeId) => {
    setDeleteConfirm({ isOpen: true, id: challengeId });
  };

  const handleDeleteConfirm = async () => {
    const challengeId = deleteConfirm.id;
    setDeleteConfirm({ isOpen: false, id: null });

    try {
      const res = await authFetch(`/api/challenges/${challengeId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        loadChallenges();
      } else {
        const errorData = await res.json().catch(() => ({}));
        alert(`Failed to delete: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to delete challenge:', err);
      alert(`Error: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="challenges-page">
        <div className="challenges-loading">Loading challenges...</div>
      </div>
    );
  }

  return (
    <div className="challenges-page">
      <div className="challenges-container">
        <div className="challenges-header">
          <div className="challenges-header__left">
            <h1>Challenges/Goals</h1>
            <p>Compete with others and push your limits</p>
          </div>
          <button
            className="btn-create-challenge"
            onClick={() => setShowCreateModal(true)}
          >
            Create New Challenge/Goal
          </button>
        </div>

        <div className="challenges-tabs">
          <button
            className={`tab ${activeTab === 'my' ? 'active' : ''}`}
            onClick={() => setActiveTab('my')}
          >
            My Challenges/Goals
          </button>
          <button
            className={`tab ${activeTab === 'public' ? 'active' : ''}`}
            onClick={() => setActiveTab('public')}
          >
            Public Challenges/Goals
          </button>
        </div>

        {activeTab === 'my' ? (
          <div className="challenges-grid">
            {myChallenges.length === 0 ? (
              <div className="challenges-empty">
                <p>You haven't joined any challenges/goals yet.</p>
                <div className="challenges-empty__actions">
                  <button
                    className="btn-primary"
                    onClick={() => setActiveTab('public')}
                  >
                    Browse Public Challenges/Goals
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    Create Challenge/Goal
                  </button>
                </div>
              </div>
            ) : (
              myChallenges.map(challenge => (
                <ChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  isJoined={true}
                  showProgress={true}
                  isCreator={user?.id === challenge.created_by}
                  onLeave={() => handleLeave(challenge.id)}
                  onEdit={() => handleEdit(challenge)}
                  onDelete={() => handleDeleteClick(challenge.id)}
                  onViewLeaderboard={() => handleViewLeaderboard(challenge)}
                />
              ))
            )}
          </div>
        ) : (
          <div className="challenges-grid">
            {challenges.length === 0 ? (
              <div className="challenges-empty">
                <p>No public challenges/goals available yet.</p>
                <button
                  className="btn-primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create the First Challenge/Goal
                </button>
              </div>
            ) : (
              challenges.map(challenge => (
                <ChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  isJoined={myChallenges.some(c => c.id === challenge.id)}
                  isCreator={user?.id === challenge.created_by}
                  onJoin={() => handleJoin(challenge.id)}
                  onLeave={() => handleLeave(challenge.id)}
                  onEdit={() => handleEdit(challenge)}
                  onDelete={() => handleDeleteClick(challenge.id)}
                  onViewLeaderboard={() => handleViewLeaderboard(challenge)}
                />
              ))
            )}
          </div>
        )}
      </div>

      {(showCreateModal || editingChallenge) && (
        <CreateChallengeModal
          onClose={() => {
            setShowCreateModal(false);
            setEditingChallenge(null);
          }}
          onCreated={handleChallengeCreated}
          challenge={editingChallenge}
        />
      )}

      {selectedChallenge && (
        <ChallengeLeaderboard
          challenge={selectedChallenge}
          onClose={() => setSelectedChallenge(null)}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Challenge"
        message="Are you sure you want to delete this challenge? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
      />
    </div>
  );
};

export default ChallengesPage;
